import { staticValue } from '@assets/scripts/api/config';
import Field from '@assets/scripts/components/field';
import { isEmpty } from 'lodash';
import { getKeyFieldForFunctionList } from '@assets/scripts/components/function-lists';

/***********************/
/******* GENERAL *******/
/***********************/

// STRING

/**
 * Check if a string is not empty
 *
 * @param {Any} value
 *  Value to check
 *
 * @returns {Boolean}
 */
const stringNotEmpty = (value) => {
	return typeof value === 'string' && value.trim() !== '';
};

/**
 * Check if a number is not empty
 *
 * @param {Any} value
 *  Value to check
 *
 * @returns {Boolean}
 */
const numberNotEmpty = (value) => {
	return typeof value === 'number';
};

/**
 * Check if a Boolean is not empty
 *
 * @param {Any} value
 *  Value to check
 *
 * @returns {Boolean}
 */
const booleanNotEmpty = (value) => {
	return typeof value === 'boolean';
};

/**
 * Check if a string has minimum 2 characters
 *
 * @param {String} value
 *  String to check
 *
 * @returns {Boolean}
 */
const stringHasAtleastTwoCharacters = (value) => {
	return typeof value === 'string' && value.length >= 2;
};

/**
 * Check if flow name matches the regex /
 *
 * @param {String} value
 *  String to check
 *
 * @returns {Boolean}
 */
const flowNameDoesNotMatchRegex = (value) => {
	return value.match('(^[/])|([^A-Za-z0-9_/])|(\\/\\/)|([/]$)');
};

/**
 * Check if a string is without spacing
 *
 * @param {String} value
 *  String to check
 *
 * @returns {Boolean}
 */
const stringWithoutSpacing = (value) => {
	return !/\s/.test(value);
};

/**
 * Check if two strings match
 *
 * @param {String} value1
 *  First value to check
 *
 * @param {String} value2
 *  Second value to check
 *
 * @returns {Boolean}
 */
const stringsMatch = (value1, value2) => {
	return valuesMatch(value1, value2);
};

/**
 * Check if a value is unique in a list
 *
 * @param {String} value
 *  Value to check
 *
 * @param {Array} list
 *  List of values
 *
 * @returns {Boolean}
 */
const stringIsUniqueInList = (value, list) => {
	return !list.some((item, idx) => {
		return list.indexOf(item) !== idx && value.toLowerCase() === item.toLowerCase();
	});
};

/**
 * Check if referenceList exist in a list
 *
 * @param {String} fieldRegex
 *  fieldRegex to check
 *
 * @param {Array/Object} list
 *  List of values
 *
 * @returns {Boolean}
 */
const checkIfReferenceListExist = (list, fieldRegex) => {
	return list.includes(fieldRegex);
};

/**
 * Check if foreignReferenceList exist in a list
 *
 * @param {String} fieldRegex
 *  fieldRegex to check
 *
 * @param {Array/Object} list
 *  List of values
 *
 * @returns {Boolean}
 */
const checkIfForeignReferenceListExist = (list, fieldRegex) => {
	let result = false;
	list.forEach((obj) => {
		if(obj.value === fieldRegex) {
			result = true;
		};
	});
	return result;
};

/**
 * Check if a value exists in a list
 *
 * @param {String} value
 *  Value to check
 *
 * @param {Array} list
 *  List of values
 *
 * @returns {Boolean}
 */
const stringExistsInList = (value, list) => {
	return list.includes(value);
};

// OBJECT & ARRAY

/**
 * Check for non empty object
 *
 * @param {Any} input
 *  Value to check
 *
 * @returns {Boolean}
 */
const isNonEmptyObject = (input = false) => {
	return (typeof input === 'object' && !isEmpty(input));
};

/**
 * Check for non empty array
 *
 * @param {Any} input
 *  Value to check
 *
 * @returns {Boolean}
 */
const isNonEmptyArray = (array) => {
	return Array.isArray(array) && array.length > 0;
};

/**
 * Counts the objects inside a list that have a given property
 * that matches the given value
 *
 * @param {Array} list
 *  List of objects to check
 *
 * @param {String} prop
 *  Name of the property to find
 *
 * @param {Any} val
 *  Value of the property to count
 *
 * @returns {Integer}
 */
const countObjectsWithProperty = (list, prop, val = true) => {
	let matches = [];

	// check if list is non-emtpy
	if (isNonEmptyArray(list)) {
		// start filtering
		matches = list.filter((el) => {
			// check if element is object with prop that matches type
			// of given value to check
			if (!isNonEmptyObject(el) || typeof el[prop] !== typeof val) {
				return false;
			}

			// strings are matched trimmed and lowercased
			if (typeof val === 'string') {
				return el[prop].trim().toLowerCase() === val.trim().toLowerCase();
			} else {
				return el[prop] === val;				
			}
		});
	}

	return matches.length;
};

/**
 * Checks if the given value of a given prop is unique within
 * a list of object
 *
 * @param {Array} list
 *  List of objects to check
 *
 * @param {String} prop
 *  Name of the property to check
 *
 * @param {Any} val
 *  Value of the property to count
 *
 * @returns {Boolean}
 */
const propIsUnique = (list, prop, val = true) => {
	return countObjectsWithProperty(list, prop, val) <= 1;
};

// FIELD

/**
 * Check if a field has a type
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldHasType = (field) => {
	return stringNotEmpty(field.type);
};

/**
 * Check if a field type is known
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldTypeExists = (field) => {
	return Field.getVarType(field.type);
};

/**
 * Check if static default has value
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldHasStaticDefault = (field) => {
	return (
		// field does not use static default
		!valuesMatch(field.default.type, staticValue) ||
		// field of type Boolean and configured value is boolean
		(field.type === 'Boolean' && booleanNotEmpty(field.default.value)) ||
		// number entered as default value
		(['Number', 'Longnumber'].includes(field.type) && numberNotEmpty(field.default.value)) ||
		// string entered as default value
		(field.type === 'String' && stringNotEmpty(field.default.value))
	);
};

/**
 * Check if validation max length match between two fields
 *
 * @param {Object} value
 * Value that should match the other value
 *
 * @param {Object} valueToMatch
 * Value to compare too
 *
 * @param {Boolean} matchOrLess
 * Indicates whether 'value' can be less
 * than 'valueToMatch' to still 'match'
 *
 * @returns {Boolean}
 */
const fieldValidationsMaxLengthMatch = (
	value,
	valueToMatch,
	matchOrLess = false
) => {
	// if valueToMatch is not set, result is true
	if (typeof valueToMatch !== 'number') return true;
	
	// if valueToMatch is a number, but value is not, result is false
	if (typeof value !== 'number') return false;

	// check for exact match
	if (!matchOrLess) return value === valueToMatch;
	// check for match or less
	else return value <= valueToMatch;
};

/**
 * Check if validation regex match between two fields
 *
 * @param {Object} validation1
 *  Validation object 1
 *
 * @param {object} validation2
 * Validation object 1
 *
 * @returns {Boolean}
 */
const fieldValidationsRegexMatch = (validation1, validation2) => {
	return valuesMatch(validation1.regex, validation2.regex);
};

/**
 * Checks if a field with a given name exists in
 * given list of fields
 *
 * @param {String} name
 *  name of field to check
 * 
 * @param {Array} list
 *  List of available fields
 *
 * @returns {Boolean}
 */
const fieldExistsInList = (name, list) => {
	return list.some((field) => stringsMatch(field.name, name));
}

/**
 * Check if type of key input in function list match input type of a given field
 *
 * @param {object} functionList
 *  Function list in the validation input
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldTypeMatchesFunctionListKeyFieldType = (
	functionList,
	field
) => {
	const keyField = getKeyFieldForFunctionList(functionList);

	return (
		keyField &&
		stringsMatch(keyField.type, field.type)
	);
};

/**
 * Check if max length of key input in function list matches max length of a given field
 *
 * @param {object} functionList
 *  Function list in the validation input
 *
 * @param {String} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldTypeMaxLengthMatchesFunctionListKeyFieldMaxLength = (
	functionList,
	field
) => {
	const keyField = getKeyFieldForFunctionList(functionList);

	return fieldValidationsMaxLengthMatch(field.validation.max, keyField.validation.max);
};

/**
 *	Checks if max length validation is set for a given field
 * 
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldMaxLengthSet = (field) => {
	return (
		field &&
		field.validation &&
		field.validation.max &&
		typeof field.validation.max === 'number' &&
		field.validation.max > 0
	);
}

/***********************/
/**** FLOW BUILDER *****/
/***********************/

// ADD BLOCK

/**
 * Check if system default in ADD block has value
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldHasSystemDefaultInAddBlock = (field) => {
	return (!valuesMatch(field.add_type, 'System') || stringNotEmpty(field.value));
};

/**
 * Check if static default in ADD block has value
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {Boolean}
 */
const fieldHasStaticDefaultInAddBlock = (field) => {
	return (
		// field does not use static default
		!valuesMatch(field.add_type, 'Static') ||
		// field of type Boolean and configured value is boolean
		(field.type === 'Boolean' && typeof field.value === 'boolean') ||
		// string entered as default value
		stringNotEmpty(field.value)
	);
};

/**
 * Check if default function has been chosen
 *
 * @param {Object} field
 *  Field to check
 *
 * @returns {boolean}
 */
 const fieldHasFunctionDefault = (field) => {
	return (!valuesMatch(field.add_type, 'Function') || field.function_guid);
};

// READ BLOCK

/**
 * Check if sort object has either direction ASC or DESC
 *
 * @param {Object} 	sortObject
 *  sort object of field
 *
 * @returns {Boolean}
 */
const isAscOrDesc = (sortObject) => {
	return ['ASC', 'DESC'].includes(sortObject.direction);
};

// RESULT BLOCK

/**
 * Check if given field used as output exists in list of fields
 *
 * @param {Object} output
 *  Output field
 *
 * @param {Array} list
 *  List of fields
 *
 * @returns {Boolean}
 */
const outputExists = (output, list) => {
	return fieldExistsInList(output.name, list);
};

// FILTERING & CONDITIONS

/**
 * Check if an condition field has a right operator
 *
 * @param {Object} condition
 *  condition object
 *
 * @param {Array} operators
 *  array of operators
 *
 * @returns {Boolean}
 */
const conditionHasCorrectOperator = (condition, operators) => {
	return operators.some((operator) => {
		return (
			operator.value === condition.operator &&
			operator.types.includes(Field.getVarType(condition.type))
		);
	});
};

/**
 * Check if an condition field has a right amount of values for operator
 *
 * @param {Object} condition
 *  condition object
 *
 * @param {Array} operators
 *  array of operators
 *
 * @returns {Boolean}
 */
const conditionHasCorrectAmountOfValues = (
	condition,
	operators
) => {
	return operators.some((operator) => {
		return (
			operator.value === condition.operator &&
			operator.inputCount === condition.value.length
		);
	});
};

/**
 * Check if condition has an operator
 *
 * @param {Object} condition
 *  condition for input
 *
 * @returns {Boolean}
 */
const conditionHasOperator = (condition) => {
	return stringNotEmpty(condition.operator);
};

/**
 * Check if value 2 is greater than value 1,
 * used for 'between' operator
 *
 * @param {Object} condition
 *  condition for input
 *
 * @returns {Boolean}
 */
const conditionValue2IsGreaterThanValue1 = (
	condition
) => {
	let result = true;
	if (condition.value.length === 2) {
		return condition.value[0] < condition.value[1];
	}
	return result;
};

/**
 * Check if given field exists in list
 *
 * @param {Object} condition
 *  Condition object
 *
 * @param {Array} list
 *  List of fields
 *
 * @returns {Boolean}
 */
const fieldInConditionExists = (condition, list) => {
	return fieldExistsInList(condition.name, list);
};

/**
 * Check if field used as value in condition exists in available fields
 *
 * @param {Object} conditionValue
 *  Condition value name
 *
 * @param {Array} list
 *  List of fields
 *
 * @returns {Boolean}
 */
const valueFieldInConditionExists = (
	conditionValue,
	list
) => {
	return list.some((field) => stringsMatch(field.name, conditionValue));
};

// MAPPING

/**
 * check if at least one key field is mapped
 *
 * @param {Object} objectDefinition
 *  Object definition for the document 
 *
 * @param {Array} mappedFields
 *  List of mappings
 *
 * @returns {Boolean}
 */
const keyFieldMapped = (objectDefinition, mappedFields) => {
	return mappedFields.some((field) => {
		return objectDefinition.input.find(
			// input is a key field and mapped or input has default value for example guid or date
			// if input has default value it will get a value form BE so it does't have to be mapped
			(input) => input.key && ((valuesMatch(field.to, input.name) && stringNotEmpty(field.from)) || input.has_default)
		);
	});
};

/**
 * Check if at least two fields are mapped
 *
 * @param {Array} mappedFields
 *  List of mappings
 *
 * @returns {Boolean}
 */
const twoFieldsMapped = (mappedFields) => {
	return (
		mappedFields.filter((field) => {
			return stringNotEmpty(field.from);
		}).length >= 2
	);
};

/**
 * Check if all required fields are mapped in default function of a field
 *
 * @param {Object} fieldFunction
 *  Function definition
 *
 * @param {Array} mappedFields
 *  List of mappings
 *
 * @returns {Boolean}
 */
const requiredFieldsMappedInFunction = (
	fieldFunction,
	mappedFields
) => {
	return checkIfRequiredFieldsMapped(fieldFunction.input, mappedFields);
};

/**
 * Check if all required fields are mapped in action of a field
 *
 * @param {Object} action
 *  Action definition
 *
 * @param {Array} mappedFields
 *  List of mappings
 *
 * @returns {Boolean}
 */
const requiredFieldsMappedInAction = (action, mappedFields) => {
	return checkIfRequiredFieldsMapped(action.input, mappedFields);
};

/**
 * Check if all required fields are mapped in external connector
 *
 * @param {Object} externalConnector
 *  ExternalConnector definition
 *
 * @param {Array} mappedFields
 *  List of mappings
 *
 * @returns {Boolean}
 */
const requiredFieldsMappedInExternalConnector = (
	externalConnector,
	mappedFields
) => {
	return checkIfRequiredFieldsMapped(externalConnector.input, mappedFields);
};

/**
 * Check if given mapped field exists in list
 *
 * @param {Object} mappingObject
 *  The mapping object with a 'from' and 'to'
 *
 * @param {Array} list
 *  List of fields
 *
 * @returns {Boolean}
 */
const mappedFieldExists = (mappingObject, list) => {
	return (
		mappingObject.from === '' ||
		list.some((input) => stringsMatch(input.name, mappingObject.from))
	);
};

/**
 * Check if field type of mapped fields match
 *
 * @param {Object} field1
 *  Field object
 *
 * @param {Object} field2
 *  Field object
 *
 * @returns {Boolean}
 */
const mappedFieldTypesMatch = (field1, field2) => {
	return !field1 || !field2 || stringsMatch(field1.type, field2.type);
};

/***********************/
/** DOCUMENT BUILDER ***/
/***********************/

// ACTION DOCUMENT

/**
 * Check if field type of referencing fields match
 *
 * @param {Object} field1
 *  Field object
 *
 * @param {Object} field2
 *  Field object
 *
 * @returns {Boolean}
 */
const referencingFieldTypesMatch = (field1, field2) => {
	return !field1 || !field2 || stringsMatch(field1.type, field2.type);
};

/***********************/
/******* HELPERS *******/
/***********************/

/**
 * Check if two variables match in both type
 * and value
 *
 * @param {Any} value1
 *  First value to check
 *
 * @param {Any} value2
 *  Second value to check
 *
 * @returns {Boolean}
 */
const valuesMatch = (value1, value2) => {
	return value1 === value2;
}

/**
 * check if requirds field are mapped
 *
 * @param {Array} input
 * inputs to check with
 *
 * @param {Array} fields
 *  mappedfields in block
 *
 * @returns {Boolean}
 */
const checkIfRequiredFieldsMapped = (input, fields) => {
	let result = true;
	input.forEach((input) => {
		// input is required and mapped and input has no default value for example guid or date
		// if input has default value it will get a value form BE so it does't have to be mapped
		if (input.validation.required && result && !input.has_default) {
			result =
				fields.filter(
					(field) => valuesMatch(input.name, field.to) && stringNotEmpty(field.from)
				).length > 0;
		}
	});

	return result;
};

export default {
	// String
	stringNotEmpty,
	stringWithoutSpacing,
	stringHasAtleastTwoCharacters,
	stringsMatch,
	stringIsUniqueInList,
	stringExistsInList,
	flowNameDoesNotMatchRegex,

	// Object & array
	isNonEmptyObject,
	isNonEmptyArray,
	countObjectsWithProperty,
	propIsUnique,

	// Field
	fieldHasType,
	fieldTypeExists,
	fieldHasStaticDefault,
	fieldValidationsMaxLengthMatch,
	fieldValidationsRegexMatch,
	fieldExistsInList,
	fieldTypeMatchesFunctionListKeyFieldType,
	fieldTypeMaxLengthMatchesFunctionListKeyFieldMaxLength,
	fieldMaxLengthSet,

	// FLOWBUILDER

	// Add block
	fieldHasSystemDefaultInAddBlock,
	fieldHasStaticDefaultInAddBlock,
	fieldHasFunctionDefault,

	// Read block
	isAscOrDesc,

	// Result block
	outputExists,

	// Filtering & conditions
	conditionHasCorrectOperator,
	conditionHasCorrectAmountOfValues,
	conditionHasOperator,
	conditionValue2IsGreaterThanValue1,
	fieldInConditionExists,
	valueFieldInConditionExists,

	// Mapping
	keyFieldMapped,
	twoFieldsMapped,
	requiredFieldsMappedInFunction,
	requiredFieldsMappedInAction,
	requiredFieldsMappedInExternalConnector,
	mappedFieldExists,
	mappedFieldTypesMatch,

	// DOCUMENTBUILDER

	// Action document
	referencingFieldTypesMatch,

	// Metadata Builder
	checkIfReferenceListExist,
	checkIfForeignReferenceListExist,
};
